/**
 * The connection form's save path for "File shares (optional)" on a CONNECTED connection (plan §3.2): the field is not
 * dial-relevant, so the edit replaces the live set in place (`azure-storage:named-shares { replace }`) BEFORE the field
 * is persisted, and the record stores exactly the answer's `shares`; each refused name is reported as
 * "Not added: <name> — <message>". A disconnected connection persists the draft: the next connect starts from it.
 */
import { toast } from '@/components/ui/toaster';
import type { Connection } from '@/types/electron';
import { useConnectionStore } from '../store/connectionStore';
import { notAddedLines, replaceNamedShares } from '../../workspace/storage-explorer/files/namedShareActions';
import type { ConnectionFormValues } from './connectionFormSchema';

const sameList = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((name, index) => name === b[index]);

export async function namedSharesForSave(values: ConnectionFormValues, saved: Connection | undefined): Promise<ConnectionFormValues> {
  if (values.engine !== 'azurestorage' || !saved) return values;
  const draft = values.azureStorageNamedShares ?? [];
  if (sameList(draft, saved.azureStorageNamedShares ?? [])) return values;
  const connected = useConnectionStore.getState().activeConnections.some((active) => active.id === saved.id && active.status === 'connected');
  if (!connected) return values;
  const result = await replaceNamedShares(saved.id, draft);
  if (!result.ok) {
    toast.error({ title: 'The file shares were not changed', description: result.error.message });
    return { ...values, azureStorageNamedShares: saved.azureStorageNamedShares };
  }
  const refused = notAddedLines(result.value);
  if (refused.length > 0) toast.warning({ title: 'Some file shares were not added', description: refused.join('\n') });
  return { ...values, azureStorageNamedShares: result.value.shares.length > 0 ? result.value.shares : undefined };
}
